/**
 * IndexedDB persistence for the upload manager.
 *
 * Why this exists
 * ───────────────
 * The upload manager (uploadManager.js) holds its queue in memory.
 * If the tab crashes / network dies / phone backgrounds the PWA and
 * gets killed by iOS, the queue is lost and 12 of 15 task photos
 * never make it to the JC.
 *
 * This module persists every queued upload to IndexedDB at enqueue
 * time and removes it on successful attach. On app boot, the
 * manager calls `restorePersistedQueue()` to re-enqueue anything
 * that didn't finish.
 *
 * Storage shape (one row per upload):
 *   {
 *     id:            local upload id (matches in-memory item.id)
 *     blob:          Blob (the File body — IndexedDB stores Blobs natively)
 *     filename:      original filename
 *     mime:          content-type
 *     size:          bytes
 *     target_kind:   "jobcard_task" / "jobcard_gallery" / "ticket" / ...
 *     target_id:     id of the parent record (e.g. "jc-123:task-456")
 *     label:         human-readable label for the dock
 *     source:        upload source tag (e.g. "bug_report")
 *     attach_kind:   string key registered via registerAttachHandler()
 *     attach_payload: JSON-serialisable args for the handler
 *     created_at:    ms timestamp
 *     attempt:       0..N (re-enqueue count after restore)
 *   }
 *
 * Note: We only persist items that opt-in by supplying `attach_kind`
 * (so we know how to re-link the upload on restore). Existing callers
 * that pass `attach` as a closure still work — they just won't
 * survive a hard reload (no regression, same as before).
 */

const DB = "hv-uploads";
const STORE = "queue";
const DB_VERSION = 1;

function _openDB() {
  return new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.open(DB, DB_VERSION);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function _store(mode = "readonly") {
  const db = await _openDB();
  return db.transaction(STORE, mode).objectStore(STORE);
}

/** Save (or update) one queued upload to IndexedDB. */
export async function persistItem(row) {
  try {
    const s = await _store("readwrite");
    return new Promise((resolve, reject) => {
      const req = s.put(row);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    // Older browsers or private mode — silently degrade.
    if (typeof console !== "undefined") console.warn("[uploads] persist failed:", e?.message);
  }
}

/** Remove a persisted row (call on terminal success or user dismiss). */
export async function removePersisted(id) {
  try {
    const s = await _store("readwrite");
    return new Promise((resolve, reject) => {
      const req = s.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    if (typeof console !== "undefined") console.warn("[uploads] remove failed:", e?.message);
  }
}

/** Read every persisted row (for restore on app boot). */
export async function readAllPersisted() {
  try {
    const s = await _store("readonly");
    return new Promise((resolve, reject) => {
      const req = s.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    if (typeof console !== "undefined") console.warn("[uploads] read failed:", e?.message);
    return [];
  }
}

/** Count persisted rows (used by health/diagnostic UI). */
export async function persistedCount() {
  try {
    const s = await _store("readonly");
    return new Promise((resolve, reject) => {
      const req = s.count();
      req.onsuccess = () => resolve(req.result || 0);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return 0;
  }
}

/** Wipe everything (for "clear stuck uploads" in settings/diagnostic UI). */
export async function clearAllPersisted() {
  try {
    const s = await _store("readwrite");
    return new Promise((resolve, reject) => {
      const req = s.clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    if (typeof console !== "undefined") console.warn("[uploads] clear failed:", e?.message);
  }
}
